import { queryClient } from 'src/config/react-query';
import { AxiosResponse } from 'axios';
import { InfiniteData } from 'react-query';
import { Updater } from 'src/types/queries';
import { Category, Acknowledgement } from 'src/types/acknowledgements';
import { Post } from 'src/types/posts';
import { functionalUpdate } from 'src/utils/queries';
import {
  addReaction,
  removeReaction,
  sortReactions,
} from 'src/utils/reactions';
import { findIndexOnPaginatedPages } from 'src/utils/profile';
import { userKeys } from '../users/queries';

export const profileKeys = {
  all: () => ['profile'],
  allDetail: () => [...profileKeys.all(), 'detail'],
  detail: (id: number | string) => [...profileKeys.allDetail(), Number(id)],
  bosses: (id: number | string) => [...profileKeys.detail(id), 'bosses'],
  userList: (name: string) => [...profileKeys.all(), name],
  userListSubordinates: (name: string) => [
    ...profileKeys.all(),
    name,
    'subordinates',
  ],
  userProfileData: (id: number | string, instanceId: number | string) => [
    ...profileKeys.detail(id),
    Number(instanceId),
    'userProfileData',
  ],
  completeDataUsers: (id: number | string) => [
    ...profileKeys.detail(id),
    'complete-user',
  ],
  acknowledgements: {
    all: (id: number | string) => [
      ...profileKeys.detail(id),
      'acknowledgements',
    ],
    list: (id: number | string) => [
      ...profileKeys.acknowledgements.all(id),
      'list',
    ],
  },
  feed: {
    all: () => [...profileKeys.all(), 'posts'],
    detail: (id: number | string) => [...profileKeys.feed.all(), Number(id)],
    list: (id: number | string) => [...profileKeys.feed.detail(id), 'list'],
  },
};

export const updateUserAcknowledgements = (
  userId,
  acknowledgement: Acknowledgement,
) => {
  setUserAcknowledgementListData(userId, acknowledgement.id, acknowledgement);
};

export const removeUserAcknowledgement = (
  userId,
  acknowledgementId: number,
) => {
  queryClient.setQueryData(
    profileKeys.acknowledgements.list(userId),
    (oldData: InfiniteData<AxiosResponse<Acknowledgement[]>>) => {
      if (!oldData?.pages?.length) return oldData;

      const [pageIndex, acknowledgementIndex] = findIndexOnPaginatedPages(
        acknowledgementId,
        oldData.pages,
      );

      if (pageIndex !== -1 && acknowledgementIndex !== -1) {
        oldData.pages[pageIndex].data.splice(acknowledgementIndex, 1);
      }

      return oldData;
    },
  );
};

export const acknowledgementsEmpty = (acknowledgementsQuery): boolean => {
  const pages = acknowledgementsQuery?.data?.pages;

  return !(pages?.length > 0 && pages[0]?.data?.length > 0);
};

export const updateCategoryData = (userId: number, newCategory: Category) => {
  queryClient.setQueryData(
    profileKeys.acknowledgements.list(userId),
    (oldData: any) => {
      if (!oldData?.pages) return oldData;

      oldData.pages.forEach((page: AxiosResponse<Acknowledgement[]>) => {
        if (page?.data) {
          page.data = page.data.map(acknowledgement => {
            if (acknowledgement?.category?.id === newCategory.id) {
              acknowledgement.category = newCategory;
            }

            return acknowledgement;
          });
        }
      });

      return oldData;
    },
  );
};

export const setUserAcknowledgementListData = (
  userId: number,
  acknowledgementId: number,
  updater: Updater<Acknowledgement>,
) => {
  queryClient.setQueryData(
    profileKeys.acknowledgements.list(userId),
    (oldData: any): any => {
      if (!oldData) return oldData;

      const [pageIndex, acknowledgementIndex] = findIndexOnPaginatedPages(
        acknowledgementId,
        oldData.pages,
      );

      if (pageIndex !== -1 && acknowledgementIndex !== -1) {
        oldData.pages[pageIndex].data[acknowledgementIndex] = functionalUpdate(
          updater,
          oldData.pages[pageIndex].data[acknowledgementIndex],
        );
      }

      return oldData;
    },
  );
};

export const setUserFeedListData = (
  userId: number,
  postItemId: number,
  updater: Updater<Post>,
) => {
  queryClient.setQueryData(
    profileKeys.feed.list(userId),
    (oldData: any): any => {
      if (!oldData) return oldData;

      const [pageIndex, postIndex] = findIndexOnPaginatedPages(
        postItemId,
        oldData.pages,
      );

      if (pageIndex !== -1 && postIndex !== -1) {
        oldData.pages[pageIndex].data[postIndex] = functionalUpdate(
          updater,
          oldData.pages[pageIndex].data[postIndex],
        );
      }

      return oldData;
    },
  );
};

export const addUserAcknowledgementListDataReaction = (
  userId: number,
  acknowledgementId: number,
  emoji: string,
  unified: string,
  isLoggedUserReaction: boolean = true,
) => {
  setUserAcknowledgementListData(
    userId,
    acknowledgementId,
    oldAcknowledgement => {
      if (!oldAcknowledgement) return;

      oldAcknowledgement.reactions = addReaction(
        oldAcknowledgement.reactions,
        emoji,
        unified,
        isLoggedUserReaction,
      );

      sortReactions(oldAcknowledgement.reactions);

      return oldAcknowledgement;
    },
  );
};

export const removeUserAcknowledgementListDataReaction = (
  userId: number,
  acknowledgementId: number,
  emoji: string,
  isLoggedUserReaction: boolean = true,
) => {
  setUserAcknowledgementListData(
    userId,
    acknowledgementId,
    oldAcknowledgement => {
      if (!oldAcknowledgement) return;

      oldAcknowledgement.reactions = removeReaction(
        oldAcknowledgement.reactions,
        emoji,
        isLoggedUserReaction,
      );

      sortReactions(oldAcknowledgement.reactions);

      return oldAcknowledgement;
    },
  );
};

export const addUserFeedListDataReaction = (
  userId: number,
  postItemId: number,
  emoji: string,
  unified: string,
  isLoggedUserReaction: boolean = true,
) => {
  setUserFeedListData(userId, postItemId, oldPost => {
    if (!oldPost) return;

    oldPost.reactions = addReaction(
      oldPost.reactions,
      emoji,
      unified,
      isLoggedUserReaction,
    );

    sortReactions(oldPost.reactions);

    return oldPost;
  });
};

export const removeUserFeedListDataReaction = (
  userId: number,
  postItemId: number,
  emoji: string,
  isLoggedUserReaction: boolean = true,
) => {
  setUserFeedListData(userId, postItemId, oldPost => {
    if (!oldPost) return;

    oldPost.reactions = removeReaction(
      oldPost.reactions,
      emoji,
      isLoggedUserReaction,
    );

    sortReactions(oldPost.reactions);

    return oldPost;
  });
};

export const updateUserProfileData = (user, instance) => {
  queryClient.invalidateQueries(profileKeys.detail(user.id));
  queryClient.invalidateQueries(
    profileKeys.userProfileData(user.id, instance.id),
  );
  queryClient.invalidateQueries(profileKeys.completeDataUsers(user.id));
  queryClient.invalidateQueries(userKeys.detail(Number(user.id)));
};
